"use client";

import { useSyncExternalStore } from "react";

// The values this hook reads are fixed for the life of the page, so the
// subscribe callback never has anyone to notify.
const neverChanges = () => () => {};

/**
 * Reads a value that only exists in the browser -- window, document.cookie,
 * localStorage -- or one that Node and the browser disagree about.
 *
 * The server render and the client's first render both get `serverValue`, so
 * the two agree and hydration is clean; React then re-renders with whatever
 * `read` returns. That is the same two-pass result as setting state from an
 * effect on mount, without the cascading render, and it is what the
 * react-hooks/set-state-in-effect rule asks for.
 *
 * `read` runs on every render and its result is compared with Object.is, so it
 * must be cheap and must return a stable reference for unchanged data. Anything
 * that builds a fresh object or array has to be cached first -- see
 * getCountryOptions() in lib/country-options.ts -- or this loops forever.
 */
export function useClientValue<T>(read: () => T, serverValue: T): T {
  return useSyncExternalStore(neverChanges, read, () => serverValue);
}
